import _, { isEmpty } from 'lodash'
import * as CommunicationCurrentStatus from 'src/constants/communication-current-status'
import * as CommunicationDirection from 'src/constants/communication-direction'
import * as CommunicationDispositionStatus from 'src/constants/communication-disposition-status'
import * as CommunicationTypes from 'src/constants/communication-types'
import { mapActions, mapGetters, mapState } from 'vuex'
import { agentMixin, notificationMixin, userMixin } from 'src/plugins/mixins/index'

export default {
  mixins: [
    agentMixin,
    notificationMixin,
    userMixin
  ],

  data () {
    return {
      showIncomingCallMenu: false,
      showParkedCallMenu: false,
      isRejecting: false,
      isHangingUp: false,
      isParking: false,
      isAnsweringCall: false
    }
  },

  computed: {
    ...mapState([
      'dialer',
      'notifications',
      'ringGroups',
      'parkedCalls',
      'showIncomingCallNotification',
      'incomingTwilioRing'
    ]),

    ...mapState('auth', ['profile']),

    ...mapState('inbox', ['liveContacts']),

    ...mapGetters('wallboard', {
      liveCalls: 'getLiveCalls'
    }),

    shouldShowIncomingCallMenu () {
      // show call buttons when the incoming call notification shows up
      if (!this.showIncomingCallNotification) {
        return false
      }

      // only the call the dialer is ringing for gets answer/reject buttons
      if (!this.dialer.communication ||
        this.dialer.communication.id !== this.communication.id) {
        return false
      }

      return this.isIncomingLiveCall &&
        this.dialer.call &&
        this.dialer.call.state === 'pending' &&
        !this.isParkedCall &&
        !this.isConnectedCall
    },
    shouldShowAnsweredCallMenu () {
      return this.isActiveCall && !this.isParkedCall && !this.shouldShowIncomingCallMenu
    },
    shouldShowParkedCallMenu () {
      return this.isParkedCall
    },
    isActiveCallOwner () {
      return this.dialer && this.dialer.state === 'open' &&
        this.dialer.communication &&
        this.dialer.communication.id === this.communication.id
    },
    isParkedCall () {
      if (!this.communication) {
        return false
      }

      const found = this.parkedCalls.find(parkedCall => parkedCall.id === this.communication.id)
      return !_.isEmpty(found)
    },

    isActiveCall () {
      if (!this.communication) {
        return false
      }

      return this.dialer.call && this.dialer.call.state === 'open' &&
        this.dialer.communication && this.dialer.communication.id === this.communication.id
    },

    isConnectedCall () {
      if (!this.communication) {
        return false
      }

      // make sure that comms has the correct status and is already included in live contacts
      return this.communication.current_status2 === CommunicationCurrentStatus.CURRENT_STATUS_INPROGRESS_NEW &&
      this.communication.disposition_status2 === CommunicationDispositionStatus.DISPOSITION_STATUS_INPROGRESS_NEW &&
      this.liveContacts.findIndex(item => item.id === this.contact.id) >= 0
    },

    isIncomingLiveCall () {
      return this.communication.type === CommunicationTypes.CALL &&
        this.communication.direction === CommunicationDirection.INBOUND &&
        this.incomingCallStatuses.includes(this.communication.current_status2)
    },

    isDialerAvailable () {
      return !this.dialer.call
    },

    isDialerConnected () {
      return this.dialer.call && this.dialer.call.state === 'open'
    },

    isLiveCall () {
      if (!this.communication) {
        return false
      }

      if (this.communication.type !== CommunicationTypes.CALL) {
        return false
      }

      // keep the row live while Twilio is ringing this agent for this communication,
      // even if an out-of-order status update would otherwise drop it — same Twilio
      // single source of truth as the incoming-call pop-up
      if (this.incomingTwilioRing === this.communication.id) {
        return true
      }

      return this.liveCallStatuses.includes(this.communication.current_status2)
    },

    incomingCallStatuses () {
      return [
        CommunicationCurrentStatus.CURRENT_STATUS_RINGALL_NEW,
        CommunicationCurrentStatus.CURRENT_STATUS_RINGING_NEW,
        CommunicationCurrentStatus.CURRENT_STATUS_GREETING_NEW,
        CommunicationCurrentStatus.CURRENT_STATUS_TRANSFERRING_NEW,
        CommunicationCurrentStatus.CURRENT_STATUS_QUEUED_NEW,
        CommunicationCurrentStatus.CURRENT_STATUS_HOLD_NEW // parked
      ]
    },

    liveCallStatuses () {
      return [...this.incomingCallStatuses, ...[CommunicationCurrentStatus.CURRENT_STATUS_INPROGRESS_NEW]]
    },

    isShowCancelCallIcon () {
      return this.isIncomingLiveCall
    },

    isDialerOrAgentOnCall () {
      return this.isDialerConnected || this.isAgentOnCall
    },

    isCallWaiting () {
      if (isEmpty(this.communication)) {
        return false
      }

      // first check if the communication object has `is_call_waiting` property
      if ('is_call_waiting' in this.communication && typeof this.communication.is_call_waiting === 'boolean') {
        return this.communication.is_call_waiting
      }

      const ringGroup = this.getRingGroup(this.communication.ring_group_id)

      if (!ringGroup) {
        return false
      }

      return this.isPersonalInbox && ringGroup.should_queue && ringGroup.fishing_mode
    },

    isPersonalInbox () {
      return this.getRingGroup(this.communication.ring_group_id)?.is_personal_inbox
    }
  },

  methods: {
    ...mapActions([
      'setShowPhone',
      'setDialerParkedCall',
      'setShowIncomingCallNotification',
      'setDialerCommunication'
    ]),
    ...mapActions('inbox', ['setLiveContacts']),
    ...mapActions('wallboard', ['fetchLiveCalls']),

    getRingGroup (id) {
      return id ? this.ringGroups.find(item => item.id === id) : null
    },

    onAcceptCall (e) {
      if ((this.dialer.call && this.dialer.currentStatus === 'CALL_CONNECTED') || this.isAgentOnCall) {
        this.showIncomingCallMenu = true
        e.stopImmediatePropagation()
        return
      }

      this.isAnsweringCall = true
      const communication = {
        id: this.communication.id,
        campaignId: this.communication.campaign_id,
        contactName: this.contact.name,
        companyName: this.contact.company_name,
        contactId: this.contact.id,
        phoneNumber: this.contact.phone_number
      }

      if (this.dialer.communication && this.dialer.communication.id === communication.id) {
        this.$VueEvent.fire('answerCall')
      } else {
        this.$VueEvent.fire('answerCall', communication)
      }
      this.isAnsweringCall = false
      this.setShowPhone(true)
      e.stopImmediatePropagation()
    },

    onRejectCall (e) {
      this.isRejecting = true

      // handle active call
      if (this.dialer && this.dialer.state === 'open') {
        this.$VueEvent.fire('hangupCall')
        this.isRejecting = false
        return
      }

      this.$VueEvent.fire('rejectCall')
      this.isRejecting = false
      this.processRemoveFromNotification(this.communication)
      e.stopImmediatePropagation()
    },

    onHangUpCall (e) {
      this.$VueEvent.fire('hangupCall')
      e.stopImmediatePropagation()
    },

    onUnparkCall (e) {
      if ((this.dialer.call && this.dialer.currentStatus === 'CALL_CONNECTED') || this.isAgentOnCall) {
        this.showParkedCallMenu = true
        e.stopImmediatePropagation()
        return
      }

      if (this.dialer.call && this.dialer.currentStatus === 'WRAP_UP') {
        this.$VueEvent.fire('endWrapUp')
      }

      this.$VueEvent.fire('unparkCall', this.communication)
      this.$VueEvent.fire('togglePhone')
      e.stopImmediatePropagation()
    },

    onParkCurrentCallAndConnect () {
      this.showParkedCallMenu = false

      if (_.isEmpty(this.dialer.parkedCall)) {
        this.setDialerParkedCall(this.communication)
      }

      this.answerCommunication(true, true)
    },

    onHangupCurrentCallAndConnect () {
      this.showParkedCallMenu = false

      if (_.isEmpty(this.dialer.parkedCall)) {
        this.setDialerParkedCall(this.communication)
      }

      this.answerCommunication(false, true)
    },

    onParkCurrentCallAndAnswer () {
      this.showIncomingCallMenu = false
      this.answerCommunication(true, false)
    },

    onHangUpCurrentCallAndAnswer () {
      this.showIncomingCallMenu = false
      this.answerCommunication(false, true)
    },

    async answerCommunication (shouldPark = false, shouldHangup = false) {
      const data = {
        communication: {
          id: this.communication.id,
          campaignId: this.communication.campaign_id,
          contactName: this.contact.name,
          companyName: this.contact.company_name,
          contactId: this.contact.id,
          phoneNumber: this.contact.phone_number,
          isCallWaiting: this.isCallWaiting
        },
        shouldPark: shouldPark,
        shouldHangup: shouldHangup
      }

      await this.fetchCurrentCommunicationIfNeeded(data)

      this.$VueEvent.fire('answerCallFishing', data)
      this.setShowPhone(true)
    },

    async fetchCurrentCommunicationIfNeeded (data = {}) {
      // Check if we need to fetch current communication
      const needsCurrentCommunication = !this.dialer.call && this.isAgentOnCall
      if (needsCurrentCommunication) {
        try {
          const response = await this.$axios.post('/api/v1/profile/get-live-calls')
          const currentCommunication = response.data.find(call => {
            return call.current_status2 === CommunicationCurrentStatus.CURRENT_STATUS_INPROGRESS_NEW
          })

          if (!currentCommunication) {
            console.log('No live calls found for this agent')
            return data
          }

          console.log('Setting dialer communication from live-calls.mixin', currentCommunication)
          this.setDialerCommunication(currentCommunication)

          data['parkFromAnotherTab'] = true

          return data
        } catch (err) {
          console.log(err)
        }
      }

      return data
    }
  }
}
